/**
 * @fileoverview The catalog's vocabulary → the `accountAppsUi` key that words it (APP-ENTITLEMENT-001 Phase 9): a
 * size dimension's unit (singular, for a price; plural, for a count), a consumable, an anchor's capability (the
 * feature a card lists), a suggestion's reason. Explicit maps, never a case transform: every value the catalog
 * vocabulary holds is named here, and the tests prove the maps TOTAL over it, so a vocabulary the catalog gains
 * cannot render a raw key.
 *
 * @module @puffin-core/account-apps-ui/copy/catalogCopy
 */

/** `DIMENSION_KEY` → the unit, singular ("$129.95 per location a month"). */
export const UNIT_KEY_BY_DIMENSION = Object.freeze({
  account: 'unitAccount',
  location: 'unitLocation',
  register: 'unitRegister',
  site: 'unitSite',
  line: 'unitLine',
  aiReceptionist: 'unitAiReceptionist',
  mailbox: 'unitMailbox',
  employeePaid: 'unitEmployeePaid',
  storefront: 'unitStorefront',
  office: 'unitOffice',
  technician: 'unitTechnician',
});

/** `DIMENSION_KEY` → the unit, plural ("Locations"). */
export const UNITS_KEY_BY_DIMENSION = Object.freeze({
  account: 'unitsAccount',
  location: 'unitsLocation',
  register: 'unitsRegister',
  site: 'unitsSite',
  line: 'unitsLine',
  aiReceptionist: 'unitsAiReceptionist',
  mailbox: 'unitsMailbox',
  employeePaid: 'unitsEmployeePaid',
  storefront: 'unitsStorefront',
  office: 'unitsOffice',
  technician: 'unitsTechnician',
});

/**
 * `CONSUMABLE_KEY` → its words ("AI minutes"). `aiOutcomes`: OUTCOME-SKU-CONSUMABLE-001 (owner rulings Q-AE-57 /
 * Q-AE-58) — PortPuffin's AI outcomes, 25 included per AI receptionist, blocks of 25.
 */
export const CONSUMABLE_KEY_BY_KEY = Object.freeze({
  blogPosts: 'consumableBlogPosts',
  aiMinutes: 'consumableAiMinutes',
  smsSegments: 'consumableSmsSegments',
  aiGenerations: 'consumableAiGenerations',
  aiQualifications: 'consumableAiQualifications',
  aiOutcomes: 'consumableAiOutcomes',
});

/** An anchor's capability key → the feature its card lists. */
export const FEATURE_KEY_BY_CAPABILITY = Object.freeze({
  pos: 'featurePos',
  website: 'featureWebsite',
  hosting: 'featureHosting',
  blog: 'featureBlog',
  'owner-console': 'featureOwnerConsole',
  analytics: 'featureAnalytics',
  'page-access': 'featurePageAccess',
  'field-service': 'featureFieldService',
});

/** `app_catalog_suggestions.reasonKey` → the pitch. */
export const SUGGESTION_KEY_BY_REASON = Object.freeze({
  mail_on_your_domain: 'suggestionMailOnYourDomain',
  answer_every_call: 'suggestionAnswerEveryCall',
  schedules_into_payroll: 'suggestionSchedulesIntoPayroll',
});

/**
 * @param {Object<string, string>} map
 * @param {string} name - the map, for the refusal.
 * @returns {(key: string) => string}
 */
function readerOf(map, name) {
  return (key) => {
    if (!Object.hasOwn(map, key)) {
      throw new TypeError(`${name}: no copy for ${JSON.stringify(key)}`);
    }
    return map[key];
  };
}

export const unitKeyOf = readerOf(UNIT_KEY_BY_DIMENSION, 'unitKeyOf');
export const unitsKeyOf = readerOf(UNITS_KEY_BY_DIMENSION, 'unitsKeyOf');
export const consumableKeyOf = readerOf(CONSUMABLE_KEY_BY_KEY, 'consumableKeyOf');
export const featureKeyOf = readerOf(FEATURE_KEY_BY_CAPABILITY, 'featureKeyOf');

/**
 * A suggestion's pitch, or `null` for a reason the copy does not know: a reason the console adds later is shown by
 * the app it names, never as a raw key.
 *
 * @param {string} reasonKey
 * @returns {?string}
 */
export function suggestionKeyOf(reasonKey) {
  return Object.hasOwn(SUGGESTION_KEY_BY_REASON, reasonKey)
    ? SUGGESTION_KEY_BY_REASON[reasonKey]
    : null;
}
